export enum AssociationSource {
  APP = 'APP',
  MANUAL = 'MANUAL',
  DASHBOARD = 'DASHBOARD',
  IDENTIFIER = 'IDENTIFIER',
  FACE_ID = 'FACE_ID',
  NOT_IDENTIFIED = 'NOT_IDENTIFIED',
  AUTOMATIC = 'AUTOMATIC',
}

/**
 * Raw identification sources used in API requests.
 * These values are sent to the backend and should match the API contract.
 */
export enum RawIdentificationSource {
  DRIVER_APP = 'driver_app',
  SHERLOCK_DRIVER_IBUTTON = 'sherlock_driver_ibutton',
  FACE_ID = 'face_id',
  DRIVER_ASSOCIATION_PAST = 'driver-association-past',
  VIDEO_REVIEW = 'video_review',
  PATH_REVIEW = 'path_review',
  UNKWNOWN = 'unknown',
  HERBIE_API = 'herbie-api',
  ASSIGN_API = 'assign_api',
  UPDATE_VEHICLE = 'update-vehicle',
  NOT_IDENTIFIED = 'not-identified',
}

/**
 * Single source of truth: mapping from AssociationSource to raw identification methods.
 * The first item in each array is the "canonical" value used when converting TO raw methods.
 */
const ASSOCIATION_SOURCE_MAPPINGS: Record<AssociationSource, string[]> = {
  [AssociationSource.APP]: ['driver_app'],
  [AssociationSource.MANUAL]: ['manual', 'driver-association-past', 'video_review', 'path_review', 'unknown'],
  [AssociationSource.DASHBOARD]: ['dashboard'],
  [AssociationSource.IDENTIFIER]: ['identifier', 'sherlock_driver_ibutton'],
  [AssociationSource.FACE_ID]: ['face_id'],
  [AssociationSource.NOT_IDENTIFIED]: ['not_identified', 'not-identified'],
  [AssociationSource.AUTOMATIC]: ['herbie-api', 'assign-api', 'assign_api', 'update-vehicle'],
};

/**
 * Derived reverse mapping from raw method to AssociationSource (computed once at module load).
 */
const RAW_TO_ASSOCIATION_SOURCE: Record<string, AssociationSource> = Object.entries(ASSOCIATION_SOURCE_MAPPINGS).reduce(
  (acc, [source, rawMethods]) => {
    for (const method of rawMethods) {
      acc[method.toLowerCase()] = source as AssociationSource;
    }
    return acc;
  },
  {} as Record<string, AssociationSource>
);

/**
 * Converts an array of AssociationSource values to their corresponding raw identification methods.
 * Used for API filtering queries.
 */
export const toIdentificationMethods = (sources: AssociationSource[]): string[] => sources.flatMap((source) => ASSOCIATION_SOURCE_MAPPINGS[source] ?? []);

/**
 * Converts a raw identification method string to its corresponding AssociationSource.
 * Falls back to MANUAL for unknown values.
 */
export const sourceFromIdentificationMethod = (identificationMethod: string): AssociationSource =>
  RAW_TO_ASSOCIATION_SOURCE[identificationMethod.toLowerCase()] ?? AssociationSource.MANUAL;

/**
 * Translation keys for each AssociationSource.
 * Used for displaying labels in the UI via i18n.
 */
export const associationSourceLabel: Record<AssociationSource, string> = {
  [AssociationSource.APP]: 'multiselects.association_source.driver_app',
  [AssociationSource.MANUAL]: 'multiselects.association_source.manual_association',
  [AssociationSource.DASHBOARD]: 'multiselects.association_source.dashboard_fixed_association',
  [AssociationSource.IDENTIFIER]: 'multiselects.association_source.key_or_rfid_card',
  [AssociationSource.FACE_ID]: 'multiselects.association_source.face_id',
  [AssociationSource.NOT_IDENTIFIED]: 'multiselects.association_source.no_driver_associated',
  [AssociationSource.AUTOMATIC]: 'multiselects.association_source.automatic_association',
};

/**
 * All available AssociationSource values in display order.
 */
export const ASSOCIATION_SOURCE_OPTIONS: AssociationSource[] = [
  AssociationSource.APP,
  AssociationSource.MANUAL,
  AssociationSource.DASHBOARD,
  AssociationSource.IDENTIFIER,
  AssociationSource.FACE_ID,
  AssociationSource.AUTOMATIC,
  AssociationSource.NOT_IDENTIFIED,
];
